import { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { listLookup } from '../../api/masterData';
import { useAuth } from '../../context/authContext';
import { useCounselorOptions } from '../../hooks/useCounselorOptions';
import { ExamFeeStatus, ExamFeeSummary } from '../../types/exam';
import { ExamListFilterValues, formatMoney } from './examListUtils';

const FEE_STATUS_CHIP: Record<
  ExamFeeStatus,
  { label: string; color: 'success' | 'warning' | 'info' | 'default' }
> = {
  PAID: { label: 'Paid', color: 'success' },
  PARTIALLY_PAID: { label: 'Partially paid', color: 'warning' },
  PENDING_CONFIRMATION: { label: 'Awaiting confirmation', color: 'info' },
  UNPAID: { label: 'Unpaid', color: 'default' },
};

/** Status chip for one fee, with paid/required in the tooltip. A pending
 * payment (slip uploaded, not confirmed) is shown as such but never counted
 * as paid — that's decided on the backend. */
export const FeeStatusChip = ({ fee }: { fee: ExamFeeSummary }) => {
  const chip = FEE_STATUS_CHIP[fee.status];
  return (
    <Tooltip
      title={`${fee.name}: ${formatMoney(fee.paidAmount, fee.currency)} paid of ${formatMoney(fee.requiredAmount, fee.currency)}${
        fee.hasPendingPayment ? ' · a payment is awaiting confirmation' : ''
      }`}
    >
      <Chip size="small" color={chip.color} label={chip.label} />
    </Tooltip>
  );
};

/** Search + University/Intake/Counselor filters shared by the Entrance Exam
 * and FIS Exam lists. The Counselor filter follows LeadsListPage: hidden
 * for a Counselor (their list is already only their own students) and for
 * roles that can't list counselors. */
export const ExamListFilters = ({
  values,
  onChange,
  children,
}: {
  values: ExamListFilterValues;
  onChange: (values: ExamListFilterValues) => void;
  children?: ReactNode;
}) => {
  const { user, hasPermission } = useAuth();
  const showCounselor = user?.role.name !== 'Counselor' && hasPermission('leads', 'assign');
  const { counselors } = useCounselorOptions(showCounselor);
  const universitiesQuery = useQuery({
    queryKey: ['lookup', '/master-data/universities'],
    queryFn: () => listLookup('/master-data/universities', { activeOnly: true }),
  });
  const intakesQuery = useQuery({
    queryKey: ['lookup', '/master-data/intakes'],
    queryFn: () => listLookup('/master-data/intakes', { activeOnly: true }),
  });

  const set = (patch: Partial<ExamListFilterValues>) => onChange({ ...values, ...patch });

  return (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
      <TextField
        placeholder="Search name, student # or phone…"
        size="small"
        sx={{ width: 260 }}
        value={values.search}
        onChange={(e) => set({ search: e.target.value })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        select
        size="small"
        label="University"
        sx={{ minWidth: 170 }}
        value={values.universityId}
        onChange={(e) => set({ universityId: e.target.value ? Number(e.target.value) : '' })}
      >
        <MenuItem value="">All</MenuItem>
        {universitiesQuery.data?.map((u) => (
          <MenuItem key={u.id as number} value={u.id as number}>
            {String(u.name)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Intake"
        sx={{ minWidth: 150 }}
        value={values.intakeId}
        onChange={(e) => set({ intakeId: e.target.value ? Number(e.target.value) : '' })}
      >
        <MenuItem value="">All</MenuItem>
        {intakesQuery.data?.map((i) => (
          <MenuItem key={i.id as number} value={i.id as number}>
            {String(i.name)}
          </MenuItem>
        ))}
      </TextField>
      {showCounselor && (
        <TextField
          select
          size="small"
          label="Counselor"
          sx={{ minWidth: 170 }}
          value={values.counselorId}
          onChange={(e) => set({ counselorId: e.target.value ? Number(e.target.value) : '' })}
        >
          <MenuItem value="">All</MenuItem>
          {counselors.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.firstName} {c.lastName}
            </MenuItem>
          ))}
        </TextField>
      )}
      {children}
    </Stack>
  );
};

declare module '@mui/x-data-grid' {
  interface NoRowsOverlayPropsOverrides {
    message?: string;
  }
}

/** Empty-state overlay for the exam DataGrids (message via slotProps). */
export const EmptyRows = ({ message }: { message?: string }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      px: 2,
      textAlign: 'center',
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);
